import type { Patient, Reference } from '@medplum/fhirtypes'
import type { TransactionMutationBuilder } from '@seen/core/bundle-builder'
import { ICD10_SYSTEM } from '@seen/core/codeSystems'
import { referenceDisplay } from '@seen/core/display'
import { featureFlags } from '@seen/core/featureFlags'
import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { getReferenceString } from '@seen/core/references'
import type { Icd10RafWeight } from '@seen/request-types/icd10RafWeights'
import { Badge } from '@seen/ui'
import Fuse from 'fuse.js'
import { partition, uniq } from 'lodash-es'
import { type JSX, memo, useCallback, useMemo, useState } from 'react'
import { type ExternalToast, toast } from 'sonner'
import { ConditionSectionBadge } from '#src/components/condition-picker/ConditionSectionBadge.tsx'
import { formatIcd10RafWeight } from '#src/components/condition-picker/formatIcd10RafWeight.ts'
import { rankConditionAdditions } from '#src/components/condition-picker/rankConditionAdditions.ts'
import {
  applyConditionReplacement,
  type ConditionReplacement,
  recordConditionOnOrgList,
} from '#src/components/condition-picker/replaceCondition.ts'
import { rowIcd10Codes } from '#src/components/condition-picker/rowIcd10Codes.ts'
import type { ConditionPickerCondition } from '#src/components/condition-picker/types.ts'
import {
  ConditionCreateConfirmDialog,
  type ConditionCreateConfirmValues,
} from '#src/components/ConditionCreateConfirmDialog.tsx'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '#src/components/core/Command.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { CreateCustomConditionDialog } from '#src/components/CreateCustomConditionDialog.tsx'
import { useConditionPickerData } from '#src/hooks/useConditionPickerData.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIcd10RafWeights } from '#src/hooks/useIcd10RafWeights.ts'
import { useIcd10Search } from '#src/hooks/useIcd10Search.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { findCreatedDiagnosis } from '#src/utils/findCreatedDiagnosis.ts'

interface ConditionsCommandProps {
  patientRef: Reference<Patient> | undefined
  initialSearch?: string
  onConditionSelected?: (condition: ConditionPickerCondition) => void
  /** Override the default toast position (e.g. 'top-right' to avoid overlapping fixed bottom buttons) */
  toastPosition?: ExternalToast['position']
  // When set, picking a condition replaces this existing diagnosis in place
  // (updating its code, ICD-10 and name) instead of adding a new one. Onset and
  // clinical status are preserved, so the onset/status prompt is skipped.
  replaceTarget?: Diagnosis
}

// Resolve a picked condition into the data needed to replace an existing one.
// Mirrors the add flow's per-source handling (org template vs ICD-10 search hit)
// so the resulting condition is coded identically to a fresh add.
function buildReplacement(
  condition: ConditionPickerCondition
): ConditionReplacement | null {
  if (condition.orgTemplate) {
    const template = condition.orgTemplate
    const icd10 = template.icd10Codes
      .filter((c): c is typeof c & { code: string } => !!c.code)
      .map((c) => ({ code: c.code, display: c.display }))
    if (icd10.length === 0) {
      return null
    }
    return { title: template.title, icd10, custom: false }
  }
  if (condition.icd10Result) {
    const icd = condition.icd10Result
    return {
      title: icd.name,
      icd10: [{ code: icd.code, display: icd.name }],
      custom: false,
      orgListEntry: {
        title: icd.name,
        icd10Codes: [
          { system: ICD10_SYSTEM, code: icd.code, display: icd.name },
        ],
      },
    }
  }
  return null
}

function isExistingSection(
  section: ConditionPickerCondition['section']
): boolean {
  return section === 'active' || section === 'inactive'
}

function capitalizeFirst(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

interface PendingCreate {
  condition: ConditionPickerCondition
  build: (values: ConditionCreateConfirmValues) => {
    diagnosis: Diagnosis
    extraOps?: (builder: TransactionMutationBuilder) => void
  }
}

export function ConditionsCommand({
  patientRef,
  initialSearch = '',
  onConditionSelected,
  toastPosition,
  replaceTarget,
}: ConditionsCommandProps): JSX.Element {
  const isReplace = replaceTarget !== undefined
  const [search, setSearch] = useState(initialSearch)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [createDialogName, setCreateDialogName] = useState('')
  const [pendingCreate, setPendingCreate] = useState<PendingCreate | null>(null)
  const medplum = useMedplum()
  const { isPending: isTransactionPending, mutateAsync: mutateTransaction } =
    useTransactionMutation()

  const {
    conditions,
    isLoading,
    orgConditionsList,
    existingParticipantIcd10Codes,
    existingOrganizationConditionTitles,
  } = useConditionPickerData({ patientRef })

  const hasSearch = search.trim().length > 0
  const { results: icd10Results, isLoading: isIcd10Loading } = useIcd10Search({
    query: search,
    enabled: hasSearch,
    existingParticipantIcd10Codes,
    existingOrganizationConditionTitles,
  })

  const singleCodeOrgIcd10Set = useMemo(() => {
    const set = new Set<string>()
    if (!orgConditionsList) {
      return set
    }
    for (const template of orgConditionsList.conditions) {
      const codes = template.icd10Codes
        .map((c) => c.code)
        .filter((c): c is string => !!c)
      if (codes.length === 1 && codes[0]) {
        set.add(codes[0])
      }
    }
    return set
  }, [orgConditionsList])

  const codeRanking = useFeatureFlag(featureFlags.conditionPickerCodeRanking)

  const fuse = useMemo(() => {
    if (!conditions) {
      return null
    }
    return new Fuse(conditions, {
      keys: ['title', 'icd10Code'],
      threshold: 0.4,
    })
  }, [conditions])

  const mergedConditions = useMemo(() => {
    if (!conditions) {
      return undefined
    }

    if (!hasSearch) {
      // Before any search, replace mode suggests the org list; add mode shows
      // the participant's existing conditions for quick reference.
      return isReplace
        ? conditions.filter((c) => !isExistingSection(c.section))
        : conditions.filter((c) => isExistingSection(c.section))
    }

    // Always include the participant's existing conditions in search results —
    // in replace mode they render as non-selectable "Already added" rows so the
    // user sees the condition is already present instead of being nudged to
    // create a duplicate.
    const filteredLocal = fuse
      ? fuse.search(search).map((r) => r.item)
      : conditions

    const filteredIcd10 = icd10Results.filter(
      (r) => !singleCodeOrgIcd10Set.has(r.code)
    )

    const icd10Conditions: ConditionPickerCondition[] = filteredIcd10.map(
      (result) => ({
        id: `icd10-${result.code}`,
        title: result.name,
        icd10Code: result.code,
        section: 'icd10' as const,
        icd10Result: {
          code: result.code,
          name: result.name,
          orgConditionsList,
        },
      })
    )

    const merged = [...filteredLocal, ...icd10Conditions]
    if (!codeRanking) {
      return merged
    }

    // Rank only the additions: the participant's own conditions keep their
    // display order in the group above.
    const [existing, additions] = partition(merged, (c) =>
      isExistingSection(c.section)
    )
    return [...existing, ...rankConditionAdditions(additions, search)]
  }, [
    conditions,
    hasSearch,
    fuse,
    search,
    icd10Results,
    singleCodeOrgIcd10Set,
    orgConditionsList,
    isReplace,
    codeRanking,
  ])

  const weightCodes = useMemo(() => {
    if (!mergedConditions) {
      return []
    }
    return uniq(mergedConditions.flatMap(rowIcd10Codes))
  }, [mergedConditions])
  const { weightsByCode } = useIcd10RafWeights(weightCodes, {
    icd10SearchLoading: isIcd10Loading,
  })

  const showCreateNew = useMemo(() => {
    if (!hasSearch || !mergedConditions || isIcd10Loading || !patientRef) {
      return false
    }
    const normalizedSearch = search.trim().toLowerCase()
    return !mergedConditions.some(
      (c) => c.title.toLowerCase() === normalizedSearch
    )
  }, [hasSearch, mergedConditions, search, isIcd10Loading, patientRef])

  const toastOptions = useMemo<ExternalToast | undefined>(
    () => (toastPosition ? { position: toastPosition } : undefined),
    [toastPosition]
  )

  const createAndNotify = useCallback(
    async (
      diagnosis: Diagnosis,
      condition: ConditionPickerCondition,
      extraOps?: (builder: TransactionMutationBuilder) => void
    ): Promise<void> => {
      try {
        const bundle = await mutateTransaction((builder) => {
          builder.create(diagnosis)
          extraOps?.(builder)
        })
        const created = findCreatedDiagnosis(bundle)
        if (!created) {
          toast.error(
            `Failed to add condition: ${condition.title}`,
            toastOptions
          )
          return
        }
        toast.success(`Added new condition: ${condition.title}`, toastOptions)
        onConditionSelected?.({ ...condition, diagnosis: created })
      } catch {
        toast.error(`Failed to add condition: ${condition.title}`, toastOptions)
      }
    },
    [mutateTransaction, onConditionSelected, toastOptions]
  )

  const replaceAndNotify = useCallback(
    async (
      target: Diagnosis,
      replacement: ConditionReplacement,
      condition: ConditionPickerCondition
    ): Promise<void> => {
      // Apply to a clone, not the cached target, so a failed transaction can't
      // leave the in-memory model ahead of the server.
      const updated = Diagnosis.fromFhirResource(
        structuredClone(target.toFhirResource())
      )
      applyConditionReplacement(updated, replacement)
      try {
        await mutateTransaction((builder) => {
          builder.update(updated.toFhirResource())
          if (replacement.orgListEntry && orgConditionsList) {
            recordConditionOnOrgList(
              orgConditionsList,
              replacement.orgListEntry,
              medplum
            )
            builder.update(orgConditionsList.toFhirResource())
          }
        })
        toast.success(`Replaced with ${replacement.title}`, toastOptions)
        onConditionSelected?.({ ...condition, diagnosis: updated })
      } catch {
        toast.error(
          `Failed to replace condition: ${replacement.title}`,
          toastOptions
        )
      }
    },
    [
      mutateTransaction,
      onConditionSelected,
      orgConditionsList,
      medplum,
      toastOptions,
    ]
  )

  const handleSelect = useCallback(
    (condition: ConditionPickerCondition) => {
      if (isTransactionPending) {
        return
      }

      // An existing participant condition is shown for reference in both modes
      // (so the user sees it's already on the chart and doesn't add a
      // duplicate). Selecting one can't create or replace anything; in replace
      // mode, surface a toast so the no-op is explained rather than silent.
      if (condition.diagnosis) {
        if (replaceTarget) {
          const participantName = referenceDisplay(patientRef)
          toast.info(
            participantName
              ? `${condition.title} is already in ${participantName}'s conditions`
              : `${condition.title} is already in this participant's conditions`,
            toastOptions
          )
        }
        onConditionSelected?.(condition)
        return
      }

      if (replaceTarget) {
        const replacement = buildReplacement(condition)
        if (!replacement) {
          toast.error(
            `Cannot replace with "${condition.title}": missing ICD-10 codes`,
            toastOptions
          )
          return
        }
        void replaceAndNotify(replaceTarget, replacement, condition)
        return
      }

      if (!patientRef) {
        toast.error('No participant selected', toastOptions)
        return
      }

      if (condition.orgTemplate) {
        const template = condition.orgTemplate
        const codes = template.icd10Codes
          .map((c) => c.code)
          .filter((c): c is string => !!c)
        if (codes.length === 0) {
          toast.error(
            `Cannot create "${condition.title}": missing ICD-10 codes`,
            toastOptions
          )
          return
        }
        setPendingCreate({
          condition,
          build: (values) => {
            const diagnosis = new Diagnosis({
              subject: patientRef,
              title: template.title,
              clinicalStatus: values.clinicalStatus,
            })
            diagnosis.icd10Codes = codes
            diagnosis.onsetDate = values.onsetDate
            return { diagnosis }
          },
        })
        return
      }

      if (condition.icd10Result) {
        const icd = condition.icd10Result
        setPendingCreate({
          condition,
          build: (values) => {
            const diagnosis = new Diagnosis({
              subject: patientRef,
              title: icd.name,
              clinicalStatus: values.clinicalStatus,
            })
            diagnosis.icd10Codes = [icd.code]
            diagnosis.onsetDate = values.onsetDate
            return {
              diagnosis,
              extraOps: (builder) => {
                if (icd.orgConditionsList) {
                  const profile = medplum.getProfile()
                  icd.orgConditionsList.add({
                    title: icd.name,
                    icd10Codes: [
                      {
                        system: ICD10_SYSTEM,
                        code: icd.code,
                        display: icd.name,
                      },
                    ],
                    snomedCodes: [],
                    addedOn: new Date().toISOString(),
                    addedBy: profile
                      ? { reference: getReferenceString(profile) }
                      : { reference: 'Organization/seen-health' },
                  })
                  builder.update(icd.orgConditionsList.toFhirResource())
                }
              },
            }
          },
        })
      }
    },
    [
      patientRef,
      onConditionSelected,
      isTransactionPending,
      medplum,
      toastOptions,
      replaceTarget,
      replaceAndNotify,
    ]
  )

  const handleConfirmCreate = useCallback(
    async (values: ConditionCreateConfirmValues): Promise<void> => {
      if (!pendingCreate) {
        return
      }
      const { diagnosis, extraOps } = pendingCreate.build(values)
      await createAndNotify(diagnosis, pendingCreate.condition, extraOps)
      setPendingCreate(null)
    },
    [pendingCreate, createAndNotify]
  )

  return (
    <>
      <Command shouldFilter={false}>
        <CommandInput
          value={search}
          onValueChange={setSearch}
          placeholder={
            isReplace ? 'Search for a replacement…' : 'Search conditions…'
          }
          className="rounded-none border-b px-3"
          autoFocus
        />
        <CommandList className="h-[300px]">
          {isLoading ? (
            <div className="flex justify-center p-4">
              <Loader />
            </div>
          ) : (
            <>
              <GroupedConditionItems
                conditions={mergedConditions}
                weightsByCode={weightsByCode}
                onSelect={handleSelect}
                additionsHeading={isReplace ? 'Replace with' : 'Add new'}
              />
              {showCreateNew && (
                <CommandGroup>
                  <CommandItem
                    value="__create_new__"
                    onSelect={() => {
                      const name = capitalizeFirst(search.trim())
                      setCreateDialogName(name)
                      setCreateDialogOpen(true)
                    }}
                  >
                    <span className="text-sm">
                      {isReplace
                        ? 'Replace with new condition: '
                        : 'Create new condition: '}
                      &ldquo;{capitalizeFirst(search.trim())}&rdquo;
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
              {hasSearch && isIcd10Loading && (
                <div className="flex justify-center p-2">
                  <Loader />
                </div>
              )}
              <CommandEmpty>
                {hasSearch
                  ? 'No matching conditions found.'
                  : isReplace
                    ? 'Search for a condition to replace this one.'
                    : patientRef
                      ? 'No conditions yet. Search above to add one.'
                      : 'Select a participant to view conditions.'}
              </CommandEmpty>
            </>
          )}
        </CommandList>
      </Command>
      {patientRef && (
        <CreateCustomConditionDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          initialName={createDialogName}
          patientRef={patientRef}
          orgConditionsList={orgConditionsList}
          replaceTarget={replaceTarget}
          onConditionCreated={(condition) => {
            setCreateDialogOpen(false)
            onConditionSelected?.(condition)
          }}
          toastPosition={toastPosition}
        />
      )}
      <ConditionCreateConfirmDialog
        open={pendingCreate !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingCreate(null)
          }
        }}
        conditionTitle={pendingCreate?.condition.title ?? ''}
        onConfirm={handleConfirmCreate}
        isPending={isTransactionPending}
      />
    </>
  )
}

const PICKER_GROUP_CLASS =
  "**:[[cmdk-group-heading]]:relative **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:pt-2 **:[[cmdk-group-heading]]:pb-1 **:[[cmdk-group-heading]]:mb-2.5 **:[[cmdk-group-heading]]:font-semibold **:[[cmdk-group-heading]]:text-foreground **:[[cmdk-group-heading]]:after:absolute **:[[cmdk-group-heading]]:after:inset-x-2 **:[[cmdk-group-heading]]:after:bottom-0 **:[[cmdk-group-heading]]:after:h-px **:[[cmdk-group-heading]]:after:bg-border **:[[cmdk-group-heading]]:after:content-['']"

const weightListCache = new WeakMap<Icd10RafWeight, readonly Icd10RafWeight[]>()

function singletonWeightList(
  weight: Icd10RafWeight
): readonly Icd10RafWeight[] {
  const cached = weightListCache.get(weight)
  if (cached) {
    return cached
  }
  const list = [weight]
  weightListCache.set(weight, list)
  return list
}

function RafColumn({
  label,
  value,
}: {
  label: 'V22' | 'V28'
  value: string
}): JSX.Element {
  return (
    <span className="flex items-center gap-1 text-xs whitespace-nowrap tabular-nums">
      <span className="text-stone-400">{label}</span>
      <span className="text-foreground">{value}</span>
    </span>
  )
}

function weightsForRow(
  condition: ConditionPickerCondition,
  weightsByCode: ReadonlyMap<string, Icd10RafWeight>
): readonly Icd10RafWeight[] | undefined {
  const codes = rowIcd10Codes(condition)
  if (codes.length === 0) {
    return undefined
  }
  const weights = codes.map((code) => weightsByCode.get(code))
  if (weights.some((weight) => weight == null)) {
    return undefined
  }
  if (weights.length === 1 && weights[0]) {
    return singletonWeightList(weights[0])
  }
  return weights as Icd10RafWeight[]
}

const ConditionItem = memo(
  function ConditionItem({
    condition,
    weights,
    onSelect,
  }: {
    condition: ConditionPickerCondition
    weights: readonly Icd10RafWeight[] | undefined
    onSelect: (condition: ConditionPickerCondition) => void
  }) {
    const raf = weights ? formatIcd10RafWeight(weights) : null
    return (
      <CommandItem
        value={condition.id}
        onSelect={() => onSelect(condition)}
        className="flex flex-col items-stretch gap-1 px-2 py-1.5"
      >
        <div className="flex w-full min-w-0 items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium">
            {condition.title}
          </span>
          <ConditionSectionBadge condition={condition} />
        </div>
        {condition.icd10Code && (
          <div className="flex w-full min-w-0 items-center justify-between gap-4">
            <Badge
              variant="code"
              className="max-w-full truncate border border-stone-200 bg-stone-100 text-xs text-stone-800"
            >
              {condition.icd10Code}
            </Badge>
            {raf && (
              <div className="flex shrink-0 items-center gap-4">
                <RafColumn label="V22" value={raf.v22} />
                <RafColumn label="V28" value={raf.v28} />
              </div>
            )}
          </div>
        )}
      </CommandItem>
    )
  },
  (prev, next) =>
    prev.condition.id === next.condition.id &&
    prev.weights === next.weights &&
    prev.onSelect === next.onSelect
)

function GroupedConditionItems({
  conditions,
  weightsByCode,
  onSelect,
  additionsHeading = 'Add new',
}: {
  conditions: ConditionPickerCondition[] | undefined
  weightsByCode: ReadonlyMap<string, Icd10RafWeight>
  onSelect: (condition: ConditionPickerCondition) => void
  additionsHeading?: string
}): JSX.Element | null {
  if (!conditions) {
    return null
  }

  const [existing, additions] = partition(conditions, (c) =>
    isExistingSection(c.section)
  )

  return (
    <>
      {existing.length > 0 && (
        <CommandGroup
          heading="Participant Conditions"
          className={PICKER_GROUP_CLASS}
        >
          {existing.map((condition) => (
            <ConditionItem
              key={condition.id}
              condition={condition}
              weights={weightsForRow(condition, weightsByCode)}
              onSelect={onSelect}
            />
          ))}
        </CommandGroup>
      )}
      {additions.length > 0 && (
        <CommandGroup heading={additionsHeading} className={PICKER_GROUP_CLASS}>
          {additions.map((condition) => (
            <ConditionItem
              key={condition.id}
              condition={condition}
              weights={weightsForRow(condition, weightsByCode)}
              onSelect={onSelect}
            />
          ))}
        </CommandGroup>
      )}
    </>
  )
}
